<template>
  <form
    id="registration"
    name="registrationForm"
    class="mt-3"
    v-if="event.id && registration.id && registrationSession.id"
    @submit.prevent="updateData(registrant, registration)">
    <!-- Page Header -->
    <div class="container-fluid">
      <div class="row">
        <div class="col">
          <div class="d-flex flex-column flex-sm-row align-items-center">
            <h3 class="mb-0 mr-3 text-center text-sm-left">
              {{registrant.full_name}}
            </h3>

            <div class="flex-fill"></div>

            <!-- Resend Confirmation Email -->
            <b-button
              type="button"
              variant="link"
              size="sm"
              @click="resendConfirmationEmail(registration.registration_session_uuid)">
              Resend Confirmation Email
            </b-button>
            <!-- / Resend Confirmation Email --><!-- Go To Transaction -->

            <!--  Go To Order -->
            <b-button
              type="button"
              variant="link"
              size="sm"
              @click="goToOrder(registrationSession.uuid)">
              Order Details
            </b-button>
            <!-- / Go To Order -->
          </div>
          <hr>
        </div>
      </div>
    </div>
    <!-- / Page Header -->

    <div class="container">
      <!-- Event Details -->
      <div class="row">
        <div class="col-sm-4">
          <label>Event:</label>
          <h6>
            {{event.name}}
          </h6>
        </div>

        <div class="col-sm-4 mt-3 mt-sm-0">
          <label>Start:</label>
          <h6>
            {{event.display_start_date_time}}
          </h6>
        </div>

        <div class="col-sm-4 mt-3 mt-sm-0">
          <label>End:</label>
          <h6>
            {{event.display_end_date_time}}
          </h6>
        </div>
      </div>
      <!-- / Event Details -->

      <!-- Registrar Details -->
      <div class="row mt-3">
        <div class="col-sm-4">
          <label>Registered By:</label>
          <h6>
            {{registrationSession.first_name}}
            {{registrationSession.middle_name}}
            {{registrationSession.last_name}}
          </h6>
        </div>

        <div class="col-sm-4 mt-3 mt-sm-0">
          <label>
            Email:
          </label>
          <h6>
            {{registrationSession.email || 'N/A'}}
          </h6>
        </div>

        <div class="col-sm-4 mt-3 mt-sm-0">
          <label>Confirmation Code:</label>
          <h6>
            {{registrationSession.short_id}}
          </h6>
        </div>
      </div>
      <!-- / Registrar Details -->

      <!-- Base Data -->
      <div class="my-3 pt-3">
        <h4 class="m-0 text-secondary">
          Registrant Profile
          <hr>
        </h4>
      </div>
      <RegistrantBaseData
        class="mt-3"
        v-model="registrant"
      />
      <!-- / Base Data -->

      <!-- Registrant Registration Data -->
      <div class="my-3 pt-3">
        <div class="d-flex align-items-center">
          <h4 class="m-0 text-secondary">Registration Form</h4>
          <!-- Filter Fields -->
          <b-dropdown
            size="sm"
            variant="link"
            :text="`Show ${fieldFilter || 'All'} Fields`">
            <b-dropdown-item
              @click="fieldFilter = 'Waiver'">
              Show Waiver Fields
            </b-dropdown-item>
            <b-dropdown-item
              @click="fieldFilter = 'Non-Waiver'">
              Show Non-Waiver Fields
            </b-dropdown-item>
            <b-dropdown-item
              @click="fieldFilter = false">
              Show All Fields
            </b-dropdown-item>
          </b-dropdown>
          <!-- / Filter Fields -->
        </div>
        <hr>
      </div>

      <FormFields
        class="mt-3"
        :form-fields="orderedFields"
        :field-filter="fieldFilter"
        v-model="registration.form_data"
        hide-buttons
      />
      <!-- / Registration Registration Data -->

      <!-- Attachments -->
      <div class="my-3 pt-3">
        <div class="d-flex align-items-center">
          <h4 class="m-0 text-secondary">
            Registrant Files
          </h4>
          <b-button
            variant="link"
            size="sm"
            class="py-0"
            :disabled="fileOptions.length === 0 || submitting"
            @click="downloadRegistrationFiles(registration.id)">
            Download All
          </b-button>
        </div>
        <hr>
      </div>
      <div class="row">
        <div class="col">
          <label for="attachments">
            Attached Files:
            <small class="text-danger">
              Total file size must be less than 30MB.
            </small>
          </label>

          <!-- Attachments Table -->
          <b-table
            striped
            hover
            small
            show-empty
            sort-by="name"
            class="file-attachments"
            :items="fileOptions"
            :fields="attachmentFields"
            :tbody-tr-class="fileToDeleteClass"
            empty-text="No attached files.">
            <template
              slot="actions"
              slot-scope="{ item }">
              <b-dropdown
                variant="link"
                boundary="window">
                <!-- Hamburger -->
                <template slot="button-content">
                  <font-awesome-icon icon="bars"></font-awesome-icon>
                </template>
                <!-- / Hamburger -->

                <!-- Options -->
                <b-dropdown-item
                  v-if="item.deleted"
                  @click="toggleAttachment(item)">
                  Undo
                </b-dropdown-item>

                <b-dropdown-item
                  v-else
                  @click="toggleAttachment(item)">
                  Remove
                </b-dropdown-item>

                <b-dropdown-item
                  @click="downloadFile(item)">
                  Download
                </b-dropdown-item>
                <!-- / Options -->
              </b-dropdown>
            </template>
            <template
              slot="created"
              slot-scope="{ item }">
              {{ item.created | datetime }}
            </template>
          </b-table>
          <!-- / Attachments Table -->
        </div>
      </div>
      <!-- / Attachments -->

      <!-- Attach New File -->
      <div class="row mt-3">
        <div class="col">
          <label for="attach-new-file">
            Upload File:
            <small class="d-block d-md-inline text-danger">
              You must click "Upload" then "Save" after selecting your file in order to attach it to this registration.
            </small>
          </label>
          <b-input-group>
            <b-form-file
              id="attach-new-file"
              ref="attachNewFile"
              name="attachNewFile"
              v-model="attachment"
              placeholder="Choose a file..."
              drop-placeholder="Drop file here..."
              :readonly="submitting">
            </b-form-file>
            <b-input-group-append>
              <b-button
                @click="attachRegistrationFile"
                :disabled="!attachment"
                variant="primary">
                Upload
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <!-- / Attach New File -->

      <div class="corner-buttons">
        <!-- Update Registration Button -->
        <b-button
          class="shadow ml-3"
          type="submit"
          variant="success"
          :disabled="submitting">
          <font-awesome-icon icon="save"></font-awesome-icon> Save
        </b-button>
        <!-- / Update Registration Button -->
      </div>
    </div>
  </form>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import DataSvc from '../services/data';
import DirtyForm from '../services/dirty-form';
import AppMixin from '../services/app-mixin';
import RegistrantBaseData from '../components/RegistrantBaseData.vue';
import FileUploadMixin from '../services/file-upload-mixin';
import EventBus from '../services/event-bus';

export default {
  name: 'registration',
  components: {
    RegistrantBaseData,
  },
  mixins: [
    DirtyForm,
    AppMixin,
    FileUploadMixin,
  ],
  data() {
    return {
      event: {},
      eventForm: {},
      registrant: {},
      registrantCopy: {},
      registration: {},
      registrationCopy: {},
      registrationSession: {},
      submitting: false,
      fieldFilter: false,
      attachmentFields: [
        {
          key: 'actions',
          label: '',
          class: 'action-column text-center',
        },
        {
          key: 'filename',
        },
        {
          key: 'created',
          label: 'Uploaded On',
        },
      ],
    };
  },
  computed: {
    orderedFields() {
      return _.orderBy(this.eventForm.form_fields, 'order');
    },
    registrantDirty() {
      const registrantJSON = JSON.stringify(this.registrant);
      const registrantCopyJSON = JSON.stringify(this.registrantCopy);
      return registrantJSON !== registrantCopyJSON;
    },
    registrationDirty() {
      const registrationJSON = JSON.stringify(this.registration);
      const registrationCopyJSON = JSON.stringify(this.registrationCopy);
      return registrationJSON !== registrationCopyJSON;
    },
    anyDirty() {
      return this.registrantDirty || this.registrationDirty;
    },
  },
  methods: {
    updateData(registrant, registration) {
      this.submitting = true;
      this.showToast('Updating registration...');

      return Promise.all([
        DataSvc.updateRegistrant(registrant),
        DataSvc.updateRegistration(registration),
      ].concat(this.updateRegistrationFiles()))
        .then((data) => {
          [this.registrant, this.registration] = data;
          this.registrantCopy = _.cloneDeep(this.registrant);
          this.registrationCopy = _.cloneDeep(this.registration);
          this.$router.push({
            name: 'event',
            params: {
              eventID: this.event.id,
              tab: 'registrants',
            },
          });
        })
        .then(() => this.delay(1000))
        .then(() => {
          this.showToast('Registration updated!');
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });
    },
    removeRegistrantFromEvent(registrationID) {
      const confirmDelete = () => {
        this.showToast('Removing registration...');
        return DataSvc.deleteRegistration(registrationID)
          .then(() => this.delay(1000))
          .then(() => {
            this.showToast('Registration removed!');
            this.$router.replace({
              name: 'event',
              params: {
                eventID: this.event.id,
                tab: 'registrants',
              },
            });
          })
          .finally(() => this.delay(1000))
          .finally(() => {
            this.submitting = false;
            this.hideToast();
          });
      };

      return this.showConfirm(
        'Are you sure you want to remove this registrant from this event?',
        'Removing a registrant',
      ).then(confirmDelete);
    },
    updateRegistrationFiles() {
      const filesToDelete = _.filter(this.fileOptions, file => file.uuid && file.deleted);
      const filesToUpload = _.filter(this.fileOptions, file => !file.uuid && !file.deleted);
      const filesToDeletePromises = [];
      const filesToUploadPromises = [];

      _.each(filesToDelete, (file) => {
        filesToDeletePromises.push(DataSvc.deleteRegistrationFile(file.uuid));
      });

      _.each(filesToUpload, (file) => {
        const data = new FormData();

        data.append('file', file.file);
        data.append('registration_id', this.registration.id);

        filesToUploadPromises.push(DataSvc.uploadRegistrationFile(data));
      });

      return Promise.all(filesToDeletePromises.concat(filesToUploadPromises));
    },
    toggleAttachment(file) {
      file.deleted = !file.deleted;
      this.$forceUpdate();
    },
    fileToDeleteClass(item) {
      return item && item.deleted ? 'b-row-selected' : '';
    },
    attachRegistrationFile() {
      const file = {
        filename: this.attachment.name,
        created: moment().format(),
        file: new File([this.attachment], this.attachment.name),
      };

      // add file to list of selectable file options
      this.fileOptions.push(file);

      // clear file upload input
      this.attachment = null;
    },
    downloadFile(file) {
      DataSvc.downloadFile(file.uuid)
        .then((data) => {
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement('a');

          link.href = url;
          link.setAttribute('download', file.filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
        });
    },
  },
  created() {
    this.showGlobalLoader('Loading registration...');

    const initRegistrant = () => {
      const { eventID, registrationID } = this.$route.params;

      // get event
      const eventPromise = DataSvc.getEvent(eventID)
        .then((event) => {
          this.event = event;

          if (this.loggedInUser.actingOrg.id !== event.org_id) {
            this.findAndSwitchActingOrg(event.org_id);
          }
        })
        .finally(() => {
          this.$nextTick(() => {
            // watch for org change and route to events page
            EventBus.$on('acting-org-changed', this.goToEvents);
          });
        });

      // get registration data
      const registrationPromise = DataSvc.getRegistration(registrationID)
        .then((registration) => {
          // set registrant
          this.registrant = registration.registrant;
          this.registrantCopy = _.cloneDeep(this.registrant);

          // set registration
          this.registration = registration;
          this.registrationCopy = _.cloneDeep(registration);

          // get registration session
          const registrationSessionPromise = DataSvc.getRegistrationSession(registration.registration_session_uuid);

          // get registration files
          const registrationFilesPromise = DataSvc.getRegistrationFiles(registrationID);

          return Promise.all([registrationSessionPromise, registrationFilesPromise]);
        })
        .then((data) => {
          [this.registrationSession, this.fileOptions] = data;
        });

      // get event form fields
      const formsPromise = DataSvc.getForms({ event_id: eventID })
        .then((eventForm) => {
          [this.eventForm] = eventForm;
        });

      return Promise.all([eventPromise, registrationPromise, formsPromise]);
    };

    this.initPromise.then(initRegistrant).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>

<style lang="scss"></style>
